import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';

/**
 * Designed empty state for data surfaces (PD-14699): muted icon, a
 * one-line message, optional supporting hint, optional action. Pages
 * decide which variant to show — truly-empty ("No invoices yet") vs
 * filtered-empty ("No invoices in this date range" + a clear/widen
 * action) — this component only owns the consistent rendering.
 *
 * Copy passes through t() here, so callers hand in plain strings.
 */
export function EmptyState({
	icon,
	title,
	hint,
	actionLabel,
	onAction,
}: {
	icon: IconDefinition;
	title: string;
	hint?: string;
	actionLabel?: string;
	onAction?: () => void;
}): JSX.Element {
	const { t } = useTranslation();
	return (
		<div className="text-center py-12 px-4">
			<FontAwesomeIcon
				icon={icon}
				className="text-4xl text-gray-300 dark:text-gray-600 mb-4"
			/>
			<p className="text-sm font-medium text-gray-900 dark:text-gray-100">
				{t(title)}
			</p>
			{hint != null && (
				<p className="text-xs text-brand-muted mt-1 max-w-xs mx-auto">
					{t(hint)}
				</p>
			)}
			{actionLabel != null && onAction != null && (
				<button
					type="button"
					onClick={onAction}
					className="mt-4 px-4 py-2 text-sm font-medium rounded-lg border border-brand-border bg-brand-surface hover:border-portal-accent transition-colors min-h-touch"
				>
					{t(actionLabel)}
				</button>
			)}
		</div>
	);
}
